import React, { useCallback, useEffect, useState } from 'react'
import { Plus, Loader2, Pencil, Archive, ArchiveRestore, CheckCircle2, Search } from 'lucide-react'
import FeaturedLayout from '../layouts/FeaturedLayout'
import { opcionesDeporte } from '../../lib/deportesOrg'
import {
  Alert,
  AlertDescription,
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input
} from '../ui'
import { plantillaAplicaAlDeporte } from './programaEditorHelpers'
import { useConfirmDialog } from '../../hooks/useConfirmDialog'
import { useOrganization } from '../../contexts/OrganizationContext'
import {
  getSeccionTemplates,
  archiveSeccionTemplate,
  restoreSeccionTemplate,
  getOrgDeportes,
  getDeporteHabilidades
} from '../../lib/supabase'
import { SECCION_TIPOS, getSeccionTipoLabel, getSeccionTipoColor } from '../../constants/seccionTipos'
import PlantillaSeccionModal from './PlantillaSeccionModal'

const formatDuracion = (totalSeg) => {
  if (!totalSeg) return '0:00'
  const min = Math.floor(totalSeg / 60)
  const seg = totalSeg % 60
  return `${min}:${seg.toString().padStart(2, '0')}`
}

const PlantillasSeccionesPage = () => {
  const { org } = useOrganization()
  const [plantillas, setPlantillas] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [filtroTipo, setFiltroTipo] = useState('todos')
  const [filtroDeporte, setFiltroDeporte] = useState('__all')
  const [orgDeportes, setOrgDeportes] = useState([])
  const [showArchived, setShowArchived] = useState(false)
  const [editorOpen, setEditorOpen] = useState(false)
  const [editandoPlantilla, setEditandoPlantilla] = useState(null)
  const [mensajeExito, setMensajeExito] = useState(null)
  const [search, setSearch] = useState('')
  const [habilidadesMap, setHabilidadesMap] = useState({})
  const { confirm, alert, ConfirmDialog } = useConfirmDialog()

  // Catalogo id -> nombre de habilidades de los deportes de la org, para mostrar chips en las tarjetas
  useEffect(() => {
    if (!org?.id) return
    let cancelled = false
    const loadHabilidades = async () => {
      const { data: orgDep } = await getOrgDeportes(org.id)
      if (!cancelled) setOrgDeportes(orgDep || [])
      const map = {}
      for (const od of orgDep || []) {
        const { data: habs } = await getDeporteHabilidades(od.deporte_id)
        for (const hab of habs || []) {
          map[hab.id] = hab.nombre
        }
      }
      if (!cancelled) setHabilidadesMap(map)
    }
    loadHabilidades()
    return () => { cancelled = true }
  }, [org?.id])

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    const tipo = filtroTipo === 'todos' ? null : filtroTipo
    const { data, error: err } = await getSeccionTemplates({
      tipo,
      incluirInactivas: showArchived
    })
    if (err) {
      setError('No pudimos cargar las plantillas')
    } else {
      setPlantillas(data || [])
    }
    setLoading(false)
  }, [filtroTipo, showArchived])

  useEffect(() => {
    load()
  }, [load])

  useEffect(() => {
    if (!mensajeExito) return
    const t = setTimeout(() => setMensajeExito(null), 2500)
    return () => clearTimeout(t)
  }, [mensajeExito])

  const handleNueva = () => {
    setEditandoPlantilla(null)
    setEditorOpen(true)
  }

  const handleEditar = (plantilla) => {
    setEditandoPlantilla(plantilla)
    setEditorOpen(true)
  }

  const handleArchivar = async (plantilla) => {
    const ok = await confirm(
      'Archivar plantilla',
      `¿Archivar "${plantilla.nombre}"? Dejara de aparecer al importar a un programa nuevo. Los programas que ya copiaron esta plantilla NO se ven afectados (son copias independientes).`,
      { destructive: true, confirmText: 'Archivar' }
    )
    if (!ok) return
    const { error: err } = await archiveSeccionTemplate(plantilla.id)
    if (err) {
      await alert('Error', err.message)
      return
    }
    setMensajeExito(`"${plantilla.nombre}" archivada`)
    await load()
  }

  const handleRestaurar = async (plantilla) => {
    const { error: err } = await restoreSeccionTemplate(plantilla.id)
    if (err) {
      await alert('Error', err.message)
      return
    }
    setMensajeExito(`"${plantilla.nombre}" restaurada`)
    await load()
  }

  const deporteLabel = (id) => {
    if (!id || id === 'todos') return 'General'
    return opcionesDeporte(orgDeportes, id).find((o) => o.value === id)?.label || id
  }

  // El filtro por tipo lo resuelve load() en el servidor; busqueda y deporte son locales.
  const term = search.trim().toLowerCase()
  const matchSearch = (p) =>
    !term ||
    (p.nombre || '').toLowerCase().includes(term) ||
    (p.descripcion || '').toLowerCase().includes(term)
  const matchDeporte = (p) => plantillaAplicaAlDeporte(p, filtroDeporte)
  const activas = plantillas.filter((p) => p.activo)
  const archivadas = plantillas.filter((p) => !p.activo)
  const visibles = (showArchived ? plantillas : activas).filter(matchSearch).filter(matchDeporte)

  return (
    <FeaturedLayout
      title="Plantillas de seccion"
      subtitle="Crea bloques reutilizables (calentamiento, hidratacion, drills) que puedes importar al construir cualquier programa de entrenamiento."
      refreshAction={load}
    >
      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {mensajeExito && (
        <Alert variant="success" className="mb-4">
          <CheckCircle2 className="h-4 w-4" />
          <AlertDescription>{mensajeExito}</AlertDescription>
        </Alert>
      )}

      {/* Toolbar */}
      <div className="flex items-center gap-2 flex-nowrap mb-3">
        <div className="flex-1 min-w-0 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar plantilla..."
            className="pl-10"
          />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground hidden sm:block whitespace-nowrap">
            <span className="font-medium text-foreground">{visibles.length}</span>{' '}
            {visibles.length === 1 ? 'plantilla' : 'plantillas'}
          </span>
          <Button onClick={handleNueva} className="h-10" aria-label="Nueva plantilla" title="Nueva plantilla">
            <Plus className="h-4 w-4 sm:mr-2" />
            <span className="hidden sm:inline">Nueva plantilla</span>
          </Button>
        </div>
      </div>

      {/* Filtro por deporte: General + los deportes de la org */}
      {orgDeportes.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 mb-2">
          {[
            { value: '__all', label: 'Todos los deportes' },
            { value: 'todos', label: 'General' },
            ...opcionesDeporte(orgDeportes)
          ].map((d) => (
            <Button
              key={d.value}
              variant={filtroDeporte === d.value ? 'default' : 'outline'}
              size="sm"
              className="h-8 rounded-full px-3 text-xs font-normal"
              onClick={() => setFiltroDeporte(d.value)}
            >
              {d.label}
            </Button>
          ))}
        </div>
      )}

      {/* Filtro por tipo como chips: son pocos y asi se ve cual esta activo */}
      <div className="flex flex-wrap items-center gap-1.5 mb-4">
        {[{ value: 'todos', label: 'Todos' }, ...SECCION_TIPOS].map((t) => (
          <Button
            key={t.value}
            variant={filtroTipo === t.value ? 'default' : 'outline'}
            size="sm"
            className="h-8 rounded-full px-3 text-xs font-normal"
            onClick={() => setFiltroTipo(t.value)}
          >
            {t.label}
          </Button>
        ))}
        {archivadas.length > 0 && (
          <>
            <span className="mx-1 h-5 w-px bg-border" />
            <Button
              variant={showArchived ? 'default' : 'outline'}
              size="sm"
              className="h-8 rounded-full px-3 text-xs font-normal"
              onClick={() => setShowArchived((s) => !s)}
            >
              Archivadas ({archivadas.length})
            </Button>
          </>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin mr-2" />
          Cargando plantillas...
        </div>
      ) : visibles.length === 0 ? (
        <div className="rounded-md border border-dashed border-input px-4 py-12 text-center text-sm text-muted-foreground">
          {term
            ? `No hay plantillas que coincidan con "${search.trim()}".`
            : filtroTipo !== 'todos'
              ? `Aun no hay plantillas del tipo "${getSeccionTipoLabel(filtroTipo)}".`
              : 'Aun no hay plantillas. Crea la primera con el boton de arriba.'}
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {visibles.map((p) => (
            <Card
              key={p.id}
              className={`group ${!p.activo ? 'opacity-60 bg-muted/30' : ''}`}
            >
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold flex items-start justify-between gap-2">
                  <span className="truncate">{p.nombre}</span>
                  <Badge variant="outline" className={`shrink-0 text-[10px] ${getSeccionTipoColor(p.tipo)}`}>
                    {getSeccionTipoLabel(p.tipo)}
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-1 pb-3 space-y-2">
                <p className="text-base font-mono font-semibold text-foreground">
                  ⏱ {formatDuracion(p.duracion_seg)}
                </p>
                {p.descripcion && (
                  <p className="text-xs text-muted-foreground line-clamp-3">{p.descripcion}</p>
                )}
                <div>
                  <Badge
                    variant="outline"
                    className={`text-[10px] font-normal ${(p.deporte && p.deporte !== 'todos') ? 'text-foreground' : 'text-muted-foreground'}`}
                  >
                    {deporteLabel(p.deporte)}
                  </Badge>
                </div>
                {(p.habilidad_ids || []).length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {p.habilidad_ids.map((hid) => (
                      habilidadesMap[hid] && (
                        <Badge key={hid} variant="outline" className="text-[10px] font-normal text-muted-foreground">
                          {habilidadesMap[hid]}
                        </Badge>
                      )
                    ))}
                  </div>
                )}
                {!p.activo && (
                  <Badge variant="outline" className="text-[10px] border-input text-muted-foreground">
                    Archivada
                  </Badge>
                )}
                <div className="flex gap-1 pt-1">
                  {p.activo ? (
                    <>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 text-xs"
                        onClick={() => handleEditar(p)}
                      >
                        <Pencil className="h-3 w-3 mr-1" />
                        Editar
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 text-xs"
                        onClick={() => handleArchivar(p)}
                      >
                        <Archive className="h-3 w-3 mr-1" />
                        Archivar
                      </Button>
                    </>
                  ) : (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 text-xs text-blue-600"
                      onClick={() => handleRestaurar(p)}
                    >
                      <ArchiveRestore className="h-3 w-3 mr-1" />
                      Restaurar
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <PlantillaSeccionModal
        open={editorOpen}
        onOpenChange={setEditorOpen}
        plantilla={editandoPlantilla}
        onSaved={() => {
          setMensajeExito(editandoPlantilla ? 'Plantilla actualizada' : 'Plantilla creada')
          load()
        }}
      />
      <ConfirmDialog />
    </FeaturedLayout>
  )
}

export default PlantillasSeccionesPage
